import { auth } from '../../../Auth/Auth';

export interface IEncryptionService {
  encrypt(data: string): Promise<string>;
  decrypt(encryptedData: string): Promise<string>;
}

export class EncryptionService implements IEncryptionService {
  private readonly saltValue = 'qna-widget-salt';
  private readonly iterations = 1000;
  private readonly keyLength = 256;
  private readonly fallbackKeyStorageKey = 'qna-fallback-key';

  private generateEncryptionKey(): string {
    const user = auth.getUser();
    const userId = user?.id?.toString();

    if (userId) {
      return userId;
    }

    let fallbackKey = localStorage.getItem(this.fallbackKeyStorageKey);
    if (!fallbackKey) {
      fallbackKey = crypto.randomUUID();
      localStorage.setItem(this.fallbackKeyStorageKey, fallbackKey);
    }

    return fallbackKey;
  }

  private async deriveKey(password: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey'],
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(this.saltValue),
        iterations: this.iterations,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: this.keyLength },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  public async encrypt(data: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const key = this.generateEncryptionKey();
      const cryptoKey = await this.deriveKey(key);
      const iv = crypto.getRandomValues(new Uint8Array(12));

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encoder.encode(data),
      );

      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      return btoa(encodeURIComponent(data));
    }
  }

  public async decrypt(encryptedData: string): Promise<string> {
    try {
      const combined = new Uint8Array(
        atob(encryptedData).split('').map((char) => char.charCodeAt(0)),
      );

      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      const key = this.generateEncryptionKey();
      const cryptoKey = await this.deriveKey(key);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encrypted,
      );

      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      try {
        return decodeURIComponent(atob(encryptedData));
      } catch {
        return encryptedData;
      }
    }
  }
}

export const encryptionService = new EncryptionService();
